import { DataLoadError, Loader } from '@creditsights/styleguide-web';
import {
  DndContext,
  DragStartEvent,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import classNames from 'classnames/bind';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ArticlePreview from '../../components/ArticlePreview';
import {
  getArticlePreviewHandler,
  OpenArticlePreviewOnClickKey,
} from '../../components/ArticlePreview/ArticlePreview.logic';
import { DragOverlayProvider } from '../../components/DragOverlay';
import { FiltersProvider } from '../../components/Filters';
import { CONTENT_TYPE_FILTER_URL_KEY } from '../../components/FiltersBar/FiltersBar.logic';
import { LockedContentView } from '../../components/LockedContent';
import { useCheckArticleAccessStatus, useFetchDefaultMyFeedArticles } from '../../hooks/articles';
import useBreakpoint from '../../hooks/common/useBreakpoint';
import useLocalStorage from '../../hooks/common/useLocalStorage';
import { useOnDragFeed } from '../../hooks/dragAndDrop';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import useFetchUserProfile from '../../hooks/profile/useFetchUserProfile';
import { ContentTypeFilter, FeedType } from '../../models';
import { ArticleTableRow } from '../../models/ArticleTableRow';
import { shouldNavigate } from '../../utils/navigationUtils';
import { getFeedType, getInitialActiveTab } from './Feed.logic';
import styles from './Feed.module.scss';
import FeedLeftContainer from './FeedLeftContainer/FeedLeftContainer';
import FeedRightContainer from './FeedRightContainer/FeedRightContainer';
import { ROUTES } from '../../Base.constants';

const cx = classNames.bind(styles);

const Feed = (): React.ReactElement => {
  const [openArticlePreviewOnClick, setOpenArticlePreviewOnClick] = useLocalStorage<boolean>(
    OpenArticlePreviewOnClickKey,
    true
  );
  const [previewArticleId, setPreviewArticleId] = useState<number | null>(null);
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const { data: userPermissions } = useFetchUserPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const userSubscriptions = userPermissions!.userSubscription;
  const tabBySubscriptions = getInitialActiveTab(userSubscriptions);
  const paramsContentType = searchParams.get(CONTENT_TYPE_FILTER_URL_KEY) as ContentTypeFilter;
  const articleTypeFilter = paramsContentType || tabBySubscriptions;
  const { onDragStart, onDragEnd, draggableState } = useOnDragFeed(articleTypeFilter);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const { rightSideWidgets, isContainerActive } = draggableState;
  const showArticlePreview = !!(isDesktop && previewArticleId);
  const { pathname } = useLocation();

  const feedType = useMemo(() => getFeedType(pathname), [pathname]);

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useFetchUserProfile();

  const checkArticleAccessStatus = useCheckArticleAccessStatus();

  const setArticleTypeFilter = (type: ContentTypeFilter) => {
    if (articleTypeFilter !== type) {
      searchParams.set(CONTENT_TYPE_FILTER_URL_KEY, type);
      setSearchParams(searchParams);
    }
  };

  useEffect(() => {
    if (feedType === FeedType.My && !userPermissions?.hasAccessToMyFeed()) {
      const globalFeedLocation = {
        pathname: ROUTES.globalFeed,
        search: createSearchParams({ type: articleTypeFilter }).toString(),
      };

      navigate(globalFeedLocation, { replace: true });
    } else if (!paramsContentType) {
      searchParams.set(CONTENT_TYPE_FILTER_URL_KEY, tabBySubscriptions);
      setSearchParams(searchParams, { replace: true });
    }
  }, [userPermissions]);

  const onPreviewableArticleClick = (article: ArticleTableRow, event: MouseEvent<HTMLElement>) => {
    const articlePreviewHandler = getArticlePreviewHandler(
      setPreviewArticleId,
      isDesktop,
      navigate,
      checkArticleAccessStatus,
      openArticlePreviewOnClick
    );
    articlePreviewHandler(article, event);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setPreviewArticleId(null);
    onDragStart(event);
  };

  const articlesQuery = useFetchDefaultMyFeedArticles(feedType);

  const isNoContent = !articlesQuery.hasResults && feedType === FeedType.My;

  if (feedType === FeedType.SpecialSituations && !userPermissions?.hasAccessToSpecialSituations()) {
    return <LockedContentView />;
  }

  if (isProfileLoading || articlesQuery.isFetching) {
    return (
      <div className={cx('loader-container')}>
        <Loader position="center" />
      </div>
    );
  }

  if (isProfileError) {
    return <DataLoadError onRetryClick={refetchProfile} />;
  }

  return (
    <div className={cx('body')}>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        collisionDetection={pointerWithin}
        sensors={sensors}
      >
        <FiltersProvider>
          <DragOverlayProvider>
            <div className={cx('item', 'left-side')}>
              <FeedLeftContainer
                feedType={feedType}
                isNoContent={isNoContent}
                onPreviewableArticleClick={onPreviewableArticleClick}
                isPreviewable={!shouldNavigate(isDesktop, openArticlePreviewOnClick)}
                resetPreviewArticleId={() => setPreviewArticleId(null)}
                previewArticleId={previewArticleId}
                subHeaderTitle={feedType === FeedType.SpecialSituations ? 'Special Situations' : undefined}
                hideSubHeaderRoutes={feedType === FeedType.SpecialSituations}
                openArticlePreviewOnClick={openArticlePreviewOnClick}
                setOpenArticlePreviewOnClick={setOpenArticlePreviewOnClick}
                isOnboardingCompleted={!!profile?.onboardingCompleted}
                articleTypeFilter={articleTypeFilter}
                setArticleTypeFilter={setArticleTypeFilter}
              />
            </div>
            {!isNoContent && (
              <div className={cx('item', 'right-side')}>
                <FeedRightContainer
                  isArticlePreviewOpen={showArticlePreview}
                  rightSideWidgets={rightSideWidgets}
                  isContainerActive={isContainerActive}
                />
                {previewArticleId && (
                  <ArticlePreview
                    articleId={previewArticleId}
                    setArticleId={setPreviewArticleId}
                    show={showArticlePreview}
                  />
                )}
              </div>
            )}
          </DragOverlayProvider>
        </FiltersProvider>
      </DndContext>
    </div>
  );
};

export default Feed;
